import React, { useState } from 'react';
import { Button } from '../../../../../../../../../components/common';
import { useMutation, useQuery } from '@tanstack/react-query';
import processorSettingService from '../../../../../../../../../services/api/payments/processorSettingService';
import { useOutletContext } from 'react-router-dom';
import ModalProcessorSettingsForm from '../../modals/ModalProcessorSettingsForm';
import useAlert from '../../../../../../../../../state/hooks/useAlert';
import useToast from '../../../../../../../../../state/hooks/useToast';
import { Menu } from '@headlessui/react';
import PopoverWithFloat from '../../../../../../../../../components/hoc/PopoverWithFloat';
import { ListState } from '../../../../../../../../../components/blocks';
import { formattedDate } from '../../../../../../../../../utils/dates';
import { capitalizeFirstLetter } from '../../../../../../../../../utils/strings';


function SchoolProcessorsSettingsListPage() {
    const { profile, paymentClient }: any = useOutletContext();

    const { addToast } = useToast();
    const { openAlertConfirm } = useAlert();
    const [processorForm, setProcessorForm] = useState<{ open: boolean; processor: any }>({ open: false, processor: null });


    const queryKey = ['payments', 'processor', 'setting'];
    //const queryClient = useQueryClient();
    const dataQuery = useQuery({
        queryKey: queryKey,
        queryFn: () => processorSettingService.getClientProcessorSettings(paymentClient.client.id),
        staleTime: Infinity,
        //retryDelay: Infinity,
        refetchOnWindowFocus: false, // Disable refetching on window focus
        refetchOnMount: true, // Disable refetching on component mount
        refetchInterval: false, // Disable interval-based refetching
    });



    const deleteMutation = useMutation({ mutationFn: (id: number) => processorSettingService.deleteSettings(paymentClient.client.id, id) });
    const handleDelete = (settings: any) => {
        openAlertConfirm({
            title: 'Delete Processor Setting',
            message: `Are you sure you want to delete ${settings.processorName} processor setting?`,
            onConfirmed: () => {
                deleteMutation.mutate(settings.id, {
                    onSuccess: () => {
                        addToast({
                            text: "Processor Setting deleted successfully",
                            type: "success",
                        });
                        dataQuery.refetch();
                    },
                    onError: () => {
                        addToast({
                            text: "Processor Setting could not be deleted",
                            type: "error",
                        });
                    }
                });
            }
        });
    }


    const statusMutation = useMutation({ mutationFn: ({ id, status }: { id: number; status: string }) => processorSettingService.updateSettings(paymentClient.client.id, id, { status }) });
    const handleActivateDeactivate = (settings: any) => {
        const action = settings.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        const actionLabel = capitalizeFirstLetter(action);

        openAlertConfirm({
            title: `${actionLabel} Processor Setting`,
            message: `Are you sure you want to ${actionLabel} ${settings.processorName}?`,
            onConfirmed: () => {
                statusMutation.mutate({ id: settings.id, status: action }, {
                    onSuccess: () => {
                        addToast({
                            text: `Processor Setting ${action === 'ACTIVE' ? 'activated' : 'deactivated'} successfully`,
                            type: "success",
                        });
                        dataQuery.refetch();
                    },
                    onError: () => {
                        addToast({
                            text: `Processor Setting could not be  ${action === 'ACTIVE' ? 'activated' : 'deactivated'}`,
                            type: "error",
                        });
                    }
                });
            }
        });
    }


    return (
        <div className='bg-white dark:bg-gray-800 shadow dark:shadow-none rounded-xl py-8 px-8'>
            <div className='flex justify-between items-start flex-wrap gap-2 mb-4'>
                <h2 className='font-medium text-xl mr-4'>Payment Processors</h2>

                {
                    // hasPermission("SMS_TOP_UP") &&
                    <Button type='button' onClick={() => setProcessorForm({ open: true, processor: null })} className=''>
                        Add Processor
                    </Button>
                }
            </div>


            {
                // if there're managers
                dataQuery.data?.data && dataQuery.data?.data.length > 0 &&
                <React.Fragment>
                    <div className='max-w-full overflow-x-auto mb-6'>
                        <table className="border-collapse table-auto w-full">
                            <thead>
                                <tr className='text-left border-b-2 dark:border-gray-700'>
                                    <th className='py-6 pl-2 pr-6'>Processor Name</th>
                                    <th className='py-6 px-6'>Data Source</th>
                                    <th className='py-6 px-6'>Channel</th>
                                    <th className='py-6 px-6'>Status</th>
                                    <th className='py-6 px-6'>Date Created</th>
                                    <th className='py-6 px-6'>Last Activated</th>
                                    <th className='py-6 px-6'>Last Deactivated</th>
                                    <th className='py-6 pl-6 pr-2 text-right'>Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y dark:divide-gray-700/60">
                                {
                                    dataQuery.data.data.map((dataItem: any) =>
                                        <tr key={dataItem?.id}>
                                            <td className='py-4 pl-2 pr-6'>{dataItem?.processorName}</td>
                                            <td className='py-4 px-6'>{dataItem?.dataSource}</td>
                                            <td className='py-4 px-6'>{dataItem?.channel}</td>
                                            <td className='py-4 px-6'>
                                                <span className={`inline-block font-medium text-sm uppercase ${dataItem.status === "ACTIVE" ? "bg-green-500/10 text-green-500" : "bg-red-500/10 text-red-500"} rounded-lg py-1 px-3`}>
                                                    {dataItem.status || "Inactive"}
                                                </span>
                                            </td>
                                            <td className='py-4 px-6'>{formattedDate(dataItem.createdDate)}</td>
                                            <td className='py-4 px-6'>{formattedDate(dataItem.lastActivated)}</td>
                                            <td className='py-4 px-6'>{formattedDate(dataItem.lastDeactivated)}</td>
                                            <td className='py-4 pl-6 pr-2 text-right'>
                                                <Menu as="div" className="relative inline-block text-left">
                                                    <PopoverWithFloat placement='bottom-end' portal>
                                                        <Menu.Button type='button' className={`w-9 h-9 transition-colors duration-300 bg-opacity-0 rounded-full hover:bg-black/5 dark:hover:bg-white/5 ui-open:bg-black/5 dark:ui-open:bg-white/5`}>
                                                            <i className="ri-more-2-fill"></i>
                                                        </Menu.Button>

                                                        <Menu.Items as='div' unmount={false} className="w-40 right-0 origin-top-right rounded-xl bg-white dark:bg-gray-900 shadow-xl dark:shadow-gray-700/60 overflow-hidden px-2 py-3">
                                                            <Menu.Item as="button" type='button' onClick={() => handleActivateDeactivate(dataItem)} className="w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg px-4 py-3">
                                                                {dataItem.status === "ACTIVE" ? 'Deactivate' : 'Activate'}
                                                            </Menu.Item>
                                                            <Menu.Item as="button" type='button' onClick={() => handleDelete(dataItem)} className="w-full text-left hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg text-red-500 px-5 py-3">
                                                                Delete
                                                            </Menu.Item>
                                                        </Menu.Items>
                                                    </PopoverWithFloat>
                                                </Menu>
                                            </td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </table>
                    </div>
                </React.Fragment>
            }

            <ListState
                // hasSearch={Object.keys(query.filters).length > 0}
                hasSearch={false}
                listCount={dataQuery.data?.data.length || 0}
                //totalDataCount={dataQuery.data?.pager.total || 0}
                totalDataCount={dataQuery.data?.data?.length || 0}
                isLoading={dataQuery.isLoading}
                isError={dataQuery.isError}
                onTryAgain={() => dataQuery.refetch()}
            />


            <ModalProcessorSettingsForm
                open={processorForm.open}
                processorSetting={null}
                client={paymentClient.client}
                onAdded={(data) => {
                    dataQuery.refetch();
                    addToast({
                        text: `Settings created successfully`,
                        type: "success"
                    });
                }}
                onUpdated={(data) => {
                    addToast({
                        text: `Settings updated successfully`,
                        type: "success"
                    });
                }}
                onClose={() => setProcessorForm((prevState) => ({ ...prevState, open: false }))}
            />
        </div>
    );
}

export default SchoolProcessorsSettingsListPage;